import { EventEmitter, Injectable } from '@angular/core';
import { BadgeStore } from './badge.store';
import { BadgeQuery } from './badge.query';
import { BadgeBackendService } from '../../services/badge/badge-backend.service';
import { BadgeRequest } from '../../models/badge/badge-request';
import { catchError, exhaustMap, map, take, tap } from 'rxjs/operators';
import { interval, of, Subscription, timer } from 'rxjs';
import { BadgeErrorResponseModel } from '../../models/badge/badge-error-response.model';
import { ModalService } from '../../services/modal.service';
import { SessionQuery } from '../session/session.query';
import { AbsenceSelectionService } from '../../services/absence/absence-selection.service';
import { CreateAskForAbsenceService } from '../../interfaces/absence/create-ask-for-absence-service';
import { AbsenceModalModel } from '../../models/absence/absence-modal.model';
import { WorkInfoOrderChangedModel } from '../../models/work-info/work-info-order-changed.model';
import { OrderItemsQuery } from '../entities/order-items/order-items.query';
import { WorkInfoCostCenterChangedModel } from '../../models/work-info/work-info-cost-center-changed.model';
import { CostCenterItemsQuery } from '../entities/cost-center-items/cost-center-items.query';
import { WorkInfoOperationChangedModel } from '../../models/work-info/work-info-operation-changed.model';
import { WorkInfoAbsenceChangedModel } from '../../models/work-info/work-info-absence-changed.model';
import { AbsenceItemsQuery } from '../entities/absence-items/absence-items.query';
import { AbsenceItemsService } from '../entities/absence-items/absence-items.service';
import { TableColumn } from '../../models/table-column';
import { OperationItemsQuery } from '../entities/operation-items/operation-items.query';
import { WorkInfoService } from '../work-info/work-info.service';
import { WorkInfoQuery } from '../work-info/work-info.query';
import { BadgeModel } from '../../models/badge/badge-model';
import { DayInfoService } from '../day-info/day-info.service';
import { ToastService } from '../../services/toast-service';
import { BarcodeBackendService } from '../../services/barcode/barcode-backend.service';
import { applyTransaction } from '@datorama/akita';
import { OrderItemsStoreService } from '../order-items-store-service';
import { LocalStateKeys } from '../local-state-keys';
import { WorkInfoStoreService } from '../work-info-store-service';
import { CostCenterItemsStoreService } from '../cost-center-items-store-service';
import { OperationItemsStoreService } from '../operation-items-store-service';
import { AlertService } from '../../services/alert.service';
import { AskForAutogehenTimeComponent } from '../../../components/ask-for-autogehen-time/ask-for-autogehen-time.component';
import * as moment from 'moment';
import {
  defaultSettingsForStandardChrono,
  getTableColumns,
} from './badge-settings.service';
import { BadgeComponentsEnum } from '../../enums/badge-components.enum';
import { MailPopUpComponent } from '../../../components/personal/personal-message/message-pop-up/mail-pop-up.component';
import { PersonalBackendService } from '../../services/personal/personal-backend.service';
import {
  BadgeAbsenceRequest,
  BarcodeParameter,
  ChronoVersionEnum,
  ManualGehenTime,
} from '../../models/client-models';
import { SettingsBackendService } from '../../services/settings/settings-backend.service';

@Injectable({
  providedIn: 'root',
})
export class BadgeService implements CreateAskForAbsenceService {
  private orderItemsQuery: OrderItemsQuery;
  private workInfoQuery: WorkInfoQuery;
  private costCenterItemsQuery: CostCenterItemsQuery;
  private operationItemsQuery: OperationItemsQuery;

  private modalSubscription = new Subscription();

  private workInfoAutoSub: Subscription | null = null;

  private emailPopUpOpen = false;

  constructor(
    private badgeStore: BadgeStore,
    private badgeQuery: BadgeQuery,
    private sessionQuery: SessionQuery,
    private badgeBackendService: BadgeBackendService,
    private orderItemsStoreService: OrderItemsStoreService,
    private workInfoStoreService: WorkInfoStoreService,
    private workInfoService: WorkInfoService,
    private modalService: ModalService,
    private alertService: AlertService,
    private toastService: ToastService,
    private absenceItemsQuery: AbsenceItemsQuery,
    private absenceItemsService: AbsenceItemsService,
    private absenceSelectionService: AbsenceSelectionService,
    private dayInfoService: DayInfoService,
    private costCenterItemsStoreService: CostCenterItemsStoreService,
    private operationItemsStoreService: OperationItemsStoreService,
    private barcodeBackendService: BarcodeBackendService,
    private personalBackendService: PersonalBackendService
  ) {
    this.orderItemsQuery = this.orderItemsStoreService.getOrCreateStore(
      LocalStateKeys.badgeOrderStoreKey
    ).query;
    this.workInfoQuery = this.workInfoStoreService.getOrCreateStore(
      LocalStateKeys.badgeWorkInfoStoreKey
    ).query;
    this.costCenterItemsQuery =
      this.costCenterItemsStoreService.getOrCreateStore(
        LocalStateKeys.badgeCostCenterStoreKey
      ).query;
    this.operationItemsQuery = this.operationItemsStoreService.getOrCreateStore(
      LocalStateKeys.badgeOperationStoreKey
    ).query;
  }

  /** Start periodic saldo refresh for the logged-in person (Badge page). */
  startLiveSaldoForBadge(intervalMs = 60_000) {
    const personId = this.sessionQuery.getPersonID();
    this.dayInfoService.startSaldoAutoRefresh(personId, intervalMs);

    // + neu: WorkInfo (Auftrag/Kostenstelle/Operation/Notiz) ebenfalls pollen
    if (this.workInfoAutoSub) {
      this.workInfoAutoSub.unsubscribe();
      this.workInfoAutoSub = null;
    }

    if (personId && personId !== 0) {
      // keine Überlappungen: exhaustMap
      this.workInfoAutoSub = timer(0, intervalMs)
        .pipe(
          exhaustMap(() =>
            this.badgeBackendService
              .getCurrentState({ idPerson: personId } as BadgeRequest)
              .pipe(
                tap((data) => this.updateStore(data)),
                catchError(() => of(null)) // Fehler beim Poll nicht eskalieren
              )
          )
        )
        .subscribe();
    }
  }

  /** Stop the periodic saldo refresh (call on page leave/destroy). */
  stopLiveSaldoForBadge() {
    this.dayInfoService.stopSaldoAutoRefresh();
    if (this.workInfoAutoSub) {
      this.workInfoAutoSub.unsubscribe();
      this.workInfoAutoSub = null;
    }
  }

  /**
   * Call getCurrentState in Backend.
   *
   * @param badgeRequest used in Backend to get the current state of the person.
   */
  getCurrentState(badgeRequest: BadgeRequest): void {
    this.badgeStore.setLoading(true);
    this.badgeBackendService
      .getCurrentState(badgeRequest)
      .pipe(tap(() => this.badgeStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.updateStore(data);
        },
        error: (error) => {
          this.badgeStore.setError<BadgeErrorResponseModel>(error.error);
          return of(error.error); // needed because catchError expects a result.
        },
      });
  }

  updateSettingsWhenChronoVersionChanges() {
    let settings;
    if (this.sessionQuery.getChronoVersion() === ChronoVersionEnum.Standard) {
      settings = defaultSettingsForStandardChrono();
    } else {
      settings = getTableColumns();
    }

    this.badgeStore.update((state) => ({
      ...state,
      settings,
    }));
  }

  /**
   * Set operation text.
   *
   * @param value new operation text.
   */
  operationSetText(value: string) {
    this.workInfoService.setOperationText(
      value,
      LocalStateKeys.badgeWorkInfoStoreKey
    );
  }

  /**
   * Execute come or go on Backend.
   *
   * @param badgeRequest used in Backend.
   */
  comeAndGo(badgeRequest: BadgeRequest): void {
    this.badgeStore.setLoading(true);

    this.badgeBackendService
      .comeAndGo(badgeRequest)
      .pipe(tap(() => this.badgeStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.updateStore(data);
        },
        error: (error) => {
          this.badgeStore.setError<BadgeErrorResponseModel>(error.error);
          this.toastService.presentErrorToast({
            message: error?.error.message,
          });
          this.badgeStore.setLoading(false);
        },
      });
  }

  /**
   * Execute come or go on Backend with absence reason.
   *
   * @param badgeRequest used in Backend.
   */
  comeAndGoAbsence(badgeRequest: BadgeAbsenceRequest): void {
    this.badgeStore.setLoading(true);

    this.badgeBackendService
      .comeAndGoAbsence(badgeRequest)
      .pipe(tap(() => this.badgeStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.updateStore(data);
        },
        error: (error) => {
          this.badgeStore.setError<BadgeErrorResponseModel>(error.error);
          this.toastService.presentErrorToast({
            message: error?.error.message,
          });
          this.badgeStore.setLoading(false);
        },
      });
  }

  /**
   * Send order change infos to backend.
   */
  orderChange() {
    this.badgeStore.setLoading(true);
    const request = {
      OldState: this.workInfoQuery.getValue()?.workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      NewOrderID: this.orderItemsQuery.getActive()?.orderID,
    } as WorkInfoOrderChangedModel;

    this.badgeBackendService
      .orderChange(request)
      .pipe(tap(() => this.badgeStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.updateStore(data);
        },
        error: (error) => {
          this.badgeStore.setError(error.error);
          this.toastService.presentErrorToast({
            message: error?.error.message,
          });
          this.badgeStore.setLoading(false);
        },
      });
  }

  /**
   * Send Cost Center change infos to backend.
   */
  costCenterChange() {
    this.badgeStore.setLoading(true);
    const request = {
      OldState: this.workInfoQuery.getValue()?.workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      NewCostCenterID: this.costCenterItemsQuery.getActive()?.id,
    } as WorkInfoCostCenterChangedModel;

    this.badgeBackendService
      .costCenterChange(request)
      .pipe(tap(() => this.badgeStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.updateStore(data);
        },
        error: (error) => {
          this.badgeStore.setError(error.error);
          this.toastService.presentErrorToast({
            message: error?.error.message,
          });
          this.badgeStore.setLoading(false);
        },
      });
  }

  /**
   * When user enters or selects new operation.
   */
  operationChange() {
    this.badgeStore.setLoading(true);

    const request = {
      OldState: this.workInfoQuery.getValue()?.workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      NewOperationAksID: this.operationItemsQuery.getActive()?.aksPlanID,
      OperationText: this.workInfoQuery.getValue()?.workInfo?.operation?.text,
    } as WorkInfoOperationChangedModel;

    if (this.operationItemsQuery.getActive()) {
      request.OperationText = this.operationItemsQuery.getActive()?.operation;
    }

    this.badgeBackendService
      .operationChange(request)
      .pipe(tap(() => this.badgeStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.updateStore(data);
        },
        error: (error) => {
          this.badgeStore.setError(error.error);
          this.toastService.presentErrorToast({
            message: error?.error.message,
          });
          this.badgeStore.setLoading(false);
        },
      });
  }

  /**
   * When user selects a absence for working info.
   */
  absenceChange() {
    this.badgeStore.setLoading(true);

    const request = {
      OldState: this.workInfoQuery.getValue()?.workInfo,
      PersonID: this.sessionQuery.getValue().user?.id,
      AbsenceID: this.absenceItemsQuery.getActive()?.id,
    } as WorkInfoAbsenceChangedModel;

    this.badgeBackendService
      .absenceChange(request)
      .pipe(tap(() => this.badgeStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.updateStore(data);
        },
        error: (error) => {
          this.badgeStore.setError(error.error);
          this.toastService.presentErrorToast({
            message: error?.error.message,
          });
          this.badgeStore.setLoading(false);
        },
      });
  }

  /**
   * Show Modal to select a absence reason.
   */
  askForAbsenceModal(): AbsenceModalModel {
    return this.absenceSelectionService.createAskForAbsenceModal();
  }

  sendBarcode(barcode: string) {
    const idPerson = this.sessionQuery.getPersonID();
    this.badgeStore.setLoading(true);
    applyTransaction(() => {
      this.barcodeBackendService
        .sendBarcode({
          barcode,
          idPerson,
        } as BarcodeParameter)
        .subscribe((value) => {
          if (value?.message) {
            this.toastService.presentInfoToast({ message: value.message });
          } else {
            this.updateStore(value);
          }
          this.badgeStore.setLoading(false);
        });
    });
  }

  /**
   * Clear used item stores.
   */
  clearUsedStore() {
    this.absenceItemsService.clearCache();
  }

  saveSettings(settings: TableColumn[]) {
    const chronoVersion = this.sessionQuery.getChronoVersion();
    for (const setting of settings) {
      if (chronoVersion === ChronoVersionEnum.Standard) {
        if (
          setting.name !== BadgeComponentsEnum.WorkInfo &&
          setting.name !== BadgeComponentsEnum.Saldo &&
          setting.name !== BadgeComponentsEnum.Expense &&
          setting.name !== BadgeComponentsEnum.Note
        ) {
          setting.disabled = true;
          setting.selected = false;
        }
      } else {
        setting.disabled = false;
      }
    }

    // console.log(settings, 'badge store saveSettings');

    this.badgeStore.update((state) => ({
      ...state,
      settings,
    }));
  }

  setAutoGehen(time: string) {
    const currentTimeEntry =
      this.badgeQuery.getValue()?.data?.dayInfo?.currentTimeEntry;

    const gehenTime = moment(time, 'hh:mm')
      .set('year', moment(currentTimeEntry.uzeit).year())
      .set('month', moment(currentTimeEntry.uzeit).month())
      .set('date', moment(currentTimeEntry.uzeit).date())
      .format();

    this.badgeBackendService
      .goWithTime({
        gehenTime,
        idPerson: this.sessionQuery.getPersonID(),
      } as ManualGehenTime)
      .subscribe((data) => {
        this.updateStore(data);
        this.badgeStore.setLoading(false);
      });
  }

  private updateStore(data: BadgeModel) {
    if (data.warnings !== '') {
      if (data.warnings === 'AUTO_GEHEN_MULTI_ORDER') {
        const onSave = new EventEmitter<string>();
        const onCancel = new EventEmitter<void>();

        this.modalService.showModal(AskForAutogehenTimeComponent, {
          createGehenEvent: onSave,
          cancelEvent: onCancel,
        });
        this.modalSubscription.add(
          onSave.subscribe((value) => {
            this.badgeStore.setLoading(true);
            this.modalService.dismissModal();
            this.setAutoGehen(value);
          })
        );

        this.modalSubscription.add(
          onCancel.subscribe(() => {
            this.modalService.dismissModal();
            this.modalSubscription.unsubscribe();
          })
        );

        return;
      }

      if (data.warnings.toLocaleLowerCase().indexOf('autogehen') !== -1) {
        this.alertService.showOkAlert(data.warnings, 'Autogehen!', null);
      }

      this.toastService.presentWarningToast({ message: data.warnings });
    }

    if (data?.eMailMessage !== '') {
      this.showEmailPopUp(data?.eMailMessage);
    }

    this.badgeStore.update((state) => ({
      ...state,
      data: {
        dayInfo: data.dayInfo,
        comeAndGo: data.comeAndGo,
      },
    }));
    this.workInfoService.updateWorkInfo(
      data?.workInfo,
      LocalStateKeys.badgeWorkInfoStoreKey,
      LocalStateKeys.badgeOrderStoreKey
    );
    this.dayInfoService.getSaldo({
      IDPerson: this.sessionQuery.getPersonID(),
      Date: new Date(),
    });
  }

  showEmailPopUp(message: string) {
    if (this.emailPopUpOpen) {
      return;
    }
    this.emailPopUpOpen = true;

    const deleteEvent = new EventEmitter<void>();
    const dontDeleteEvent = new EventEmitter<void>();

    this.modalService.showModal(MailPopUpComponent, {
      message,
      deleteEvent,
      dontDeleteEvent,
    });

    deleteEvent.subscribe(() => {
      this.emailPopUpOpen = false;
      this.deleteEmail();
    });

    dontDeleteEvent.subscribe(() => {
      this.emailPopUpOpen = false;
    });
  }

  deleteEmail() {
    this.personalBackendService
      .deletePersonalEmail(this.sessionQuery.getPersonID())
      .subscribe({
        next: (value) => {
          this.toastService.presentSuccessToast({ message: value.message });
        },
        error: (error) =>
          this.toastService.presentErrorToast({ message: error.error.message }),
      });
  }
}
